import React, { useState } from "react";
import { toast } from 'react-toastify';
import { callbackUrl } from '../../../config/clinicConfig';
import { trackCallbackSuccess } from '../../../utils/gtag';

const CallbackForm: React.FC = () => {
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        message: ""
    });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<"idle" | "success">("idle");

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name.trim() || !formData.phone.trim() || !formData.message.trim()) {
            toast.error("Please fill in all required fields.");
            return;
        }

        // Phone validation
        const phoneRegex = /^\d{10,}$/;
        const cleanPhone = formData.phone.replace(/\D/g, "");
        if (!phoneRegex.test(cleanPhone)) {
            toast.error("Please enter a valid phone number (10+ digits).");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(callbackUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    phone: cleanPhone,
                    message: formData.message.trim()
                }),
            });

            if (response.ok) {
                const result = await response.json();
                console.log("Callback request successful:", result);
                toast.success("Request submitted successfully! We'll call you back soon.");
                trackCallbackSuccess();
                setStatus("success");
                setFormData({ name: "", phone: "", message: "" });
            } else {
                throw new Error(`Server error: ${response.status}`);
            }
        } catch (error) {
            console.error("Callback request failed:", error);
            toast.error("Failed to submit request. Please try again later.");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="footer-card d-flex flex-column text-center p-4 bg-white rounded shadow-sm border border-light-subtle">
            <h5 className="fw-bold mb-3 text-dark">Request Call Back</h5>

            {status === "success" ? (
                <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1">
                    <i className="bi bi-check-circle-fill text-success fs-1 mb-3"></i>
                    <p className="text-success fw-bold mb-2">Request Submitted!</p>
                    <p className="text-muted small mb-3">We'll call you back soon.</p>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm rounded-pill"
                        onClick={() => setStatus("idle")}
                    >
                        Submit Another Request
                    </button>
                </div>
            ) : (
                <form className="d-flex flex-column flex-grow-1" onSubmit={handleSubmit}>
                    <div className="mb-3 text-start">
                        <label htmlFor="nameInput" className="form-label small text-muted">
                            <i className="bi bi-person me-1 text-dark"></i>Your Name{" "}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control border-secondary-subtle rounded-pill transition-all focus-shadow-grey"
                            id="nameInput"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your Name"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="mb-3 text-start">
                        <label htmlFor="phoneInput" className="form-label small text-muted">
                            <i className="bi bi-telephone me-1 text-dark"></i>Telephone{" "}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="tel"
                            className="form-control border-secondary-subtle rounded-pill transition-all focus-shadow-grey"
                            id="phoneInput"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="1234567890"
                            required
                            disabled={loading}
                        />
                    </div>

                    {/* New Message Field */}
                    <div className="mb-3 text-start">
                        <label htmlFor="messageInput" className="form-label small text-muted">
                            <i className="bi bi-chat-left-text me-1 text-dark"></i>Message
                        </label>
                        <textarea
                            className="form-control border-secondary-subtle rounded transition-all focus-shadow-grey"
                            id="messageInput"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Your message here..."
                            rows={4}
                            disabled={loading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-black w-100 rounded-pill mt-auto transition-all hover-lift"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                            </>
                        ) : (
                            "Submit"
                        )}
                    </button>
                </form>

            )}
        </div>
    );
};

export default CallbackForm;
